// Dashboard Page - Modern Design
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, Truck, Calendar, Clock, AlertTriangle, 
  ChevronRight, TrendingUp, Activity, ArrowUpRight, Gauge, GraduationCap
} from 'lucide-react';
import { useAuthStore, useWacheStore } from '../lib/store';
import { wacheApi, dienstApi, usersApi, einteilungenApi, praktikantenApi, featureFlagsApi } from '../lib/api';
import { formatDate, roleLabels, dienstArtLabels, cn } from '../lib/utils';
import { DIENSTGRAD_CONFIG, getHelmetForDienstgrad } from '../lib/dienstgrade';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { FAHRZEUG_BILDER, getFahrzeugBildByTypCode, useFahrzeugBilder } from '../lib/fahrzeugBilder';

import { Shirt } from 'lucide-react';

// Fahrzeug-Bilder Mapping (Re-export für Abwärtskompatibilität)
const vehicleImages = FAHRZEUG_BILDER;

function WelcomeHeader({ user, wache, helmConfig }) {
  const currentHour = new Date().getHours();
  let greeting = 'Guten Tag';
  if (currentHour < 12) greeting = 'Guten Morgen';
  else if (currentHour >= 18) greeting = 'Guten Abend';

  const helmetSrc = getHelmetForDienstgrad(user?.dienstgrad, helmConfig);
  const dienstgradCfg = DIENSTGRAD_CONFIG[(user?.dienstgrad || '').toUpperCase()];

  return (
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-primary to-red-700 p-8 text-white">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>
      
      <div className="relative">
        <p className="text-white/80 text-lg">{greeting},</p>
        <h1 className="text-4xl font-heading font-bold mt-1">
          {user?.vorname} {user?.nachname}
        </h1>
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <Badge className="bg-white/20 hover:bg-white/30 text-white border-0">
            {roleLabels[user?.rolle]}
          </Badge>
          <span className="text-white/60">•</span>
          <span className="text-white/80">{wache?.name}</span>
          {dienstgradCfg && (
            <>
              <span className="text-white/60">•</span>
              <div
                className="flex items-center gap-2 bg-white/10 rounded-md px-2 py-1"
                data-testid="welcome-dienstgrad-insignia"
                title={dienstgradCfg.label}
              >
                <img
                  src={dienstgradCfg.image}
                  alt={dienstgradCfg.label}
                  className="h-7 w-auto drop-shadow"
                />
                <span className="text-xs font-medium text-white/90 hidden sm:inline">
                  {dienstgradCfg.label}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Diensthelm rechts unten - auf mobil ausgeblendet (Platzgründe) */}
      <div className="hidden sm:block absolute right-4 bottom-2 sm:right-6 sm:bottom-3 opacity-95 pointer-events-none select-none">
        <img
          src={helmetSrc}
          alt="Diensthelm"
          className="w-32 sm:w-36 lg:w-44 drop-shadow-2xl"
          data-testid="welcome-diensthelm"
        />
      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, description, trend, gradient }) {
  return (
    <div className={cn(
      'relative overflow-hidden rounded-2xl p-6 transition-all hover:shadow-lg',
      gradient || 'bg-white dark:bg-gray-900 border'
    )}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            {title}
          </p>
          <p className="mt-2 text-4xl font-bold font-heading">{value}</p>
          {description && (
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        <div className={cn(
          'p-3 rounded-xl',
          gradient ? 'bg-white/20' : 'bg-primary/10'
        )}>
          <Icon className={cn(
            'w-6 h-6',
            gradient ? 'text-white' : 'text-primary'
          )} />
        </div>
      </div>
      {trend && (
        <div className="mt-4 flex items-center gap-1 text-sm text-emerald-600">
          <TrendingUp className="w-4 h-4" />
          <span>{trend}</span>
        </div>
      )}
    </div>
  );
}

function NextShiftSection({ naechsterDienst, isLoading }) {
  if (isLoading) {
    return <Skeleton className="h-48 rounded-2xl" />;
  }

  if (!naechsterDienst?.datum) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center rounded-2xl border-2 border-dashed border-muted">
        <Clock className="w-12 h-12 text-muted-foreground/50 mb-4" />
        <p className="text-lg font-medium">Keine Einteilung gefunden</p>
        <p className="text-muted-foreground mt-1">Für die nächsten Tage ist kein Dienst geplant</p>
        <Link to="/einteilung">
          <Button className="mt-4" variant="outline">
            Einteilung erstellen
          </Button>
        </Link>
      </div>
    );
  }

  const { datum, dienst_art, einteilungen_nach_fahrzeug } = naechsterDienst;

  // Extract vehicle type from funkrufname (e.g., "LHF 4600" -> "LHF")
  const getVehicleType = (funkrufname) => {
    const match = funkrufname?.match(/^([A-Za-z]+)/);
    return match ? match[1].toUpperCase() : 'LHF';
  };

  // Reihenfolge der Funktionen wie im Magnetboard/MatrixView:
  // RTW → MVE links, Ma rechts (Rest dazwischen).
  // Andere Fahrzeuge behalten ihre originale Reihenfolge.
  const sortEinteilungenForVehicle = (typCode, einteilungen) => {
    if (typCode !== 'RTW') return einteilungen;
    const isMa = (k) => /(^|[-_])MA$|(^|[-_])Ma$|Maschinist|(^|[-_])Fa$/i.test(k || '');
    const isMve = (k) => /MVE|Beifahrer/i.test(k || '');
    const mve = einteilungen.filter(e => isMve(e.funktion_kuerzel));
    const ma = einteilungen.filter(e => isMa(e.funktion_kuerzel));
    const rest = einteilungen.filter(e => !isMve(e.funktion_kuerzel) && !isMa(e.funktion_kuerzel));
    return [...mve, ...rest, ...ma];
  };

  return (
    <div className="space-y-4">
      {/* Header - kompakter */}
      <div className="flex items-center justify-between border-b pb-3">
        <div>
          <h2 className="text-xl font-bold">Nächster Dienst</h2>
          <p className="text-sm text-muted-foreground">
            {formatDate(datum, { weekday: 'long', day: '2-digit', month: 'long' })}
          </p>
        </div>
        <Badge 
          variant={dienst_art === 'T' ? 'default' : 'secondary'}
          className="text-sm px-3 py-1"
        >
          {dienstArtLabels[dienst_art] || dienst_art}
        </Badge>
      </div>

      {/* Kompakte Kladden-Ansicht */}
      <div className="space-y-2 max-w-md">
        {Object.entries(einteilungen_nach_fahrzeug || {}).map(([fahrzeug, einteilungen]) => {
          const vehicleType = getVehicleType(fahrzeug);
          const imageUrl = vehicleImages[vehicleType] || vehicleImages.LHF;
          const sortedEinteilungen = sortEinteilungenForVehicle(vehicleType, einteilungen);
          
          return (
            <div
              key={fahrzeug}
              className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors border"
            >
              {/* Fahrzeug-Header (Bild + Name) */}
              <div className="flex items-center gap-3 sm:min-w-[140px]">
                <div className="w-12 h-8 shrink-0 flex items-center justify-center">
                  <img
                    src={imageUrl}
                    alt={vehicleType}
                    className="max-h-full max-w-full object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
                <div className="font-semibold text-sm">{fahrzeug}</div>
              </div>

              {/* Besatzung - 2 Spalten = Trupp-Anordnung (MVE+Ma, AtrF+AtrM, ...) */}
              <div className="grid grid-cols-2 gap-1 flex-1 w-full">
                {sortedEinteilungen.map((e, i) => (
                  <span
                    key={i}
                    className={cn(
                      'text-xs px-1.5 py-0.5 rounded truncate',
                      e.mitarbeiter_name
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    )}
                    title={`${e.funktion_kuerzel}: ${e.mitarbeiter_name || '—'}`}
                  >
                    <span className="font-semibold">{e.funktion_kuerzel}:</span>{' '}
                    {e.mitarbeiter_name?.split(' ').pop() || '—'}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Link zur Einteilung */}
      <Link to="/einteilung">
        <Button variant="outline" size="sm" className="w-full">
          Vollständige Einteilung
          <ArrowUpRight className="w-4 h-4 ml-2" />
        </Button>
      </Link>
    </div>
  );
}

function VehicleStatusSection({ fahrzeuge, isLoading }) {
  if (isLoading) {
    return (
      <div className="grid gap-4 grid-cols-2 sm:grid-cols-3">
        {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
      </div>
    );
  }

  // Filter echte Fahrzeuge mit funkrufname
  const aktiveFahrzeuge = fahrzeuge.filter(f => f.funkrufname && !f.ist_ausser_dienst);
  const ausserDienst = fahrzeuge.filter(f => f.ist_ausser_dienst);

  // Extract typ code from fahrzeugtyp_code (e.g., "LHF" from "LHF 4600/1")
  const getTypCode = (fz) => {
    const code = fz.fahrzeugtyp_code || fz.typ || '';
    const match = code.match(/^([A-Za-z]+)/);
    return match ? match[1].toUpperCase() : 'LHF';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Fahrzeugstatus</h2>
        <Link to="/fahrzeuge">
          <Button variant="ghost" size="sm">
            Alle anzeigen <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>

      {/* Vehicle Grid */}
      <div className="grid gap-4 grid-cols-2 sm:grid-cols-3">
        {aktiveFahrzeuge.slice(0, 6).map((fz) => {
          const typCode = getTypCode(fz);
          const imageUrl = vehicleImages[typCode] || vehicleImages.LHF;
          return (
            <Link 
              key={fz.id}
              to={`/fahrzeuge?selected=${fz.id}`}
              className="group relative p-4 rounded-xl bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 border hover:shadow-md hover:border-primary/50 transition-all cursor-pointer"
            >
              <div className="absolute top-2 right-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
              </div>
              <div className="h-16 flex items-center justify-center mb-2">
                <img 
                  src={imageUrl} 
                  alt={typCode}
                  className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
              <p className="font-semibold text-sm text-center truncate group-hover:text-primary transition-colors">{fz.funkrufname}</p>
              <p className="font-mono text-xs text-muted-foreground text-center">{fz.kennzeichen || '—'}</p>
            </Link>
          );
        })}
      </div>

      {aktiveFahrzeuge.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Truck className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>Keine Fahrzeuge vorhanden</p>
          <Link to="/fahrzeuge">
            <Button variant="outline" className="mt-4">Fahrzeuge anlegen</Button>
          </Link>
        </div>
      )}

      {/* Out of Service Alert */}
      {ausserDienst.length > 0 && (
        <div className="flex items-center gap-4 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <div className="p-2 rounded-lg bg-amber-500/20">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="font-medium text-amber-800 dark:text-amber-200">
              {ausserDienst.length} Fahrzeug{ausserDienst.length > 1 ? 'e' : ''} außer Dienst
            </p>
            <p className="text-sm text-amber-600 dark:text-amber-400">
              {ausserDienst.map(f => f.funkrufname).join(', ')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function QuickActionsSection({ user }) {
  const [activeMenuKeys, setActiveMenuKeys] = useState(null);

  useEffect(() => {
    featureFlagsApi.getActiveMenuKeys()
      .then(res => setActiveMenuKeys(res.data || []))
      .catch(() => setActiveMenuKeys([]));
  }, []);

  // Mapping action.href → menu key (für Feature-Flag-Check)
  const allActions = [
    {
      label: 'Atemschutz',
      description: 'Überwachung starten',
      icon: Gauge,
      href: '/atemschutz',
      menuKey: 'atemschutz',
      color: 'bg-amber-500',
    },
    {
      label: 'Pool-Kleidung',
      description: 'Leihkleidung verwalten',
      icon: Shirt,
      href: '/pool-kleidung',
      menuKey: 'pool-kleidung',
      color: 'bg-emerald-500',
    },
    {
      label: 'Fahrzeug takeln',
      icon: Truck,
      href: '/fahrzeuge',
      menuKey: 'fahrzeuge',
      color: 'bg-red-500',
    },
    {
      label: 'Einteilung bearbeiten',
      icon: Calendar,
      href: '/einteilung',
      menuKey: 'einteilung',
      color: 'bg-blue-500',
    },
    {
      label: 'Mitarbeiter',
      icon: Users,
      href: '/mitarbeiter',
      menuKey: 'mitarbeiter',
      color: 'bg-purple-500',
      roles: ['DEVELOPER', 'WACHLEITUNG', 'WACHABTEILUNGSLEITUNG', 'ADMIN'],
    },
  ];

  // Feature-Flag-Filter (Developer sieht immer alles).
  // Während des Ladens (activeMenuKeys === null) nichts anzeigen,
  // damit kein Flackern und keine versehentlich deaktivierten Module sichtbar werden.
  const actions = allActions.filter(a => {
    if (a.roles && !a.roles.includes(user?.rolle)) return false;
    if (activeMenuKeys === null) return false;
    if (user?.rolle === 'DEVELOPER') return true;
    return activeMenuKeys.includes(a.menuKey);
  });

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Schnellzugriff</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {actions.map((action) => (
          <Link key={action.href} to={action.href}>
            <div className="group p-6 rounded-2xl border bg-white dark:bg-gray-900 hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer h-full">
              <div className={cn(
                'w-12 h-12 rounded-xl flex items-center justify-center mb-4',
                action.color
              )}>
                <action.icon className="w-6 h-6 text-white" />
              </div>
              <p className="font-semibold group-hover:text-primary transition-colors">
                {action.label}
              </p>
              {action.description && (
                <p className="text-xs text-muted-foreground mt-1">{action.description}</p>
              )}
              <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground group-hover:text-primary transition-colors">
                <span>Öffnen</span>
                <ArrowUpRight className="w-4 h-4" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function MeinePraktikantenSection({ praktikanten }) {
  const heute = new Date().toISOString().split('T')[0];
  const typLabels = {
    RS_PRAKTIKANT: 'RS-Praktikant',
    TRUPP_MANN: 'Truppmann-Praktikant',
    SCHUELER: 'Schüler',
    SONSTIGE: 'Sonstige',
  };

  return (
    <div className="space-y-4" data-testid="meine-praktikanten-section">
      <div className="flex items-center gap-2">
        <GraduationCap className="w-5 h-5 text-amber-600" />
        <h2 className="text-xl font-heading font-semibold">Meine Praktikanten</h2>
        <Badge variant="secondary" className="bg-amber-100 text-amber-800">
          {praktikanten.length}
        </Badge>
      </div>
      <Card>
        <CardContent className="p-0 divide-y">
          {praktikanten.map((prk) => {
            const istAktiv = prk.zeitraum_von <= heute && prk.zeitraum_bis >= heute;
            const istZukuenftig = prk.zeitraum_von > heute;
            return (
              <div
                key={prk.id}
                className={cn(
                  'p-4 flex items-center gap-4',
                  istAktiv && 'bg-amber-50/40'
                )}
                data-testid={`praktikant-row-${prk.id}`}
              >
                <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold shrink-0">
                  {prk.vorname?.[0]}{prk.nachname?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold truncate">
                      {prk.vorname} {prk.nachname}
                    </span>
                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300">
                      {typLabels[prk.typ] || prk.typ}
                    </Badge>
                    {istAktiv && (
                      <Badge className="text-[10px] bg-emerald-600">
                        Aktiv
                      </Badge>
                    )}
                    {istZukuenftig && (
                      <Badge variant="outline" className="text-[10px] text-blue-700 border-blue-300">
                        Bevorstehend
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    <Calendar className="w-3 h-3" />
                    <span>
                      {formatDate(prk.zeitraum_von, { day: '2-digit', month: 'short', year: 'numeric' })}
                      {' — '}
                      {formatDate(prk.zeitraum_bis, { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                    {prk.abteilung_name && <span className="ml-2">• {prk.abteilung_name}</span>}
                  </div>
                </div>
                <Link to="/praktikanten">
                  <Button variant="ghost" size="sm">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}

export function DashboardPage() {
  const { user } = useAuthStore();
  const { wache, setWache, setAbteilungen } = useWacheStore();
  
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({ users: 0, fahrzeuge: 0, abteilungen: 0 });
  const [fahrzeuge, setFahrzeuge] = useState([]);
  const [naechsterDienst, setNaechsterDienst] = useState(null);
  const [meinePraktikanten, setMeinePraktikanten] = useState([]);
  const [helmConfig, setHelmConfig] = useState(null);
  const [activeMenuKeys, setActiveMenuKeys] = useState(null);
  useFahrzeugBilder(); // lädt Custom-Fahrzeugbilder reaktiv

  // Feature-Flags laden (für Dashboard-Karten-Sichtbarkeit)
  useEffect(() => {
    featureFlagsApi.getActiveMenuKeys()
      .then(res => setActiveMenuKeys(res.data || []))
      .catch(() => setActiveMenuKeys([]));
  }, []);

  // Developer sieht alles; sonst per Flag steuerbar.
  // Während des Ladens (null) wird die Karte standardmäßig angezeigt,
  // um Flackern zu vermeiden.
  const isCardVisible = (menuKey) => {
    if (user?.rolle === 'DEVELOPER') return true;
    if (activeMenuKeys === null) return true;
    return activeMenuKeys.includes(menuKey);
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Wichtig: dienstApi.getNaechsterDienst nutzt den Berliner 8-Tage-Rhythmus
        // der Abteilung und gibt den ECHTEN nächsten Dienst-Tag zurück (kein freier Tag).
        // Falls User keine Abteilung hat, fallback auf einteilungenApi (Datum-basiert).
        const naechsterDienstPromise = user?.abteilung_id
          ? dienstApi.getNaechsterDienst({
              abteilung_id: user.abteilung_id,
              mitarbeiter_id: user.id,
            })
          : einteilungenApi.getNaechsterDienst();

        const [wacheRes, abtRes, fzRes, usersRes, dienstRes, helmRes] = await Promise.all([
          wacheApi.get(),
          wacheApi.getAbteilungen(),
          dienstApi.getFahrzeuge({ nur_aktive: true }),
          usersApi.getAll({ ist_aktiv: true }),
          naechsterDienstPromise,
          wacheApi.getHelme().catch(() => ({ data: null })),
        ]);

        setWache(wacheRes.data);
        setAbteilungen(abtRes.data);
        setFahrzeuge(fzRes.data);
        setNaechsterDienst(dienstRes.data);
        setHelmConfig(helmRes.data);
        
        setStats({
          users: usersRes.data.length,
          fahrzeuge: fzRes.data.filter(f => !f.ist_ausser_dienst).length,
          abteilungen: abtRes.data.length,
        });

        // Meine zugewiesenen Praktikanten (für Betreuer)
        try {
          const prkRes = await praktikantenApi.getMeine();
          setMeinePraktikanten(prkRes.data || []);
        } catch {
          setMeinePraktikanten([]);
        }
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [setWache, setAbteilungen, user?.abteilung_id, user?.id]);

  return (
    <MainLayout>
      <div className="space-y-8" data-testid="dashboard">
        {/* Welcome Header */}
        <WelcomeHeader user={user} wache={wache} helmConfig={helmConfig} />

        {/* Stats Row - nur die wichtigen */}
        {(isCardVisible('dashboard-card-mitarbeiter') || isCardVisible('dashboard-card-fahrzeuge')) && (
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2',
              isCardVisible('dashboard-card-mitarbeiter') && isCardVisible('dashboard-card-fahrzeuge')
                ? 'lg:grid-cols-2'
                : 'lg:grid-cols-1'
            )}
            data-testid="dashboard-stat-row"
          >
            {isCardVisible('dashboard-card-mitarbeiter') && (
              <StatCard
                title="Mitarbeiter"
                value={isLoading ? '—' : stats.users}
                icon={Users}
                description="Aktive Teammitglieder"
              />
            )}
            {isCardVisible('dashboard-card-fahrzeuge') && (
              <StatCard
                title="Fahrzeuge"
                value={isLoading ? '—' : stats.fahrzeuge}
                icon={Truck}
                description="Einsatzbereit"
              />
            )}
          </div>
        )}

        {/* Two Column Layout */}
        {(isCardVisible('dashboard-card-naechster-dienst') || isCardVisible('dashboard-card-fahrzeugstatus')) && (
          <div
            className={cn(
              'grid gap-8',
              isCardVisible('dashboard-card-naechster-dienst') && isCardVisible('dashboard-card-fahrzeugstatus')
                ? 'lg:grid-cols-2'
                : 'lg:grid-cols-1'
            )}
          >
            {/* Next Shift */}
            {isCardVisible('dashboard-card-naechster-dienst') && (
              <div className="space-y-4" data-testid="dashboard-card-naechster-dienst">
                <NextShiftSection naechsterDienst={naechsterDienst} isLoading={isLoading} />
              </div>
            )}
            
            {/* Vehicle Status */}
            {isCardVisible('dashboard-card-fahrzeugstatus') && (
              <div className="space-y-4" data-testid="dashboard-card-fahrzeugstatus">
                <VehicleStatusSection fahrzeuge={fahrzeuge} isLoading={isLoading} />
              </div>
            )}
          </div>
        )}

        {/* Meine Praktikanten (nur sichtbar, wenn dem User Praktikanten als Betreuer zugewiesen sind) */}
        {meinePraktikanten.length > 0 && (
          <MeinePraktikantenSection praktikanten={meinePraktikanten} />
        )}

        {/* Quick Actions */}
        <QuickActionsSection user={user} />
      </div>
    </MainLayout>
  );
}

export default DashboardPage;
